/**
 * Every piece of type on the V2 surface.
 *
 * A View picks a ROLE (`variant`) and a semantic colour (`tone`); the families,
 * sizes and leadings live in `theme/tokens`'s `TYPE`, so a type overhaul is one
 * file. `no-restricted-syntax` makes a bare `fontSize`/`fontFamily` inside a
 * unit a lint error (unit convention §2).
 *
 * `numeric` is the tabular-figures switch: TYPE cannot carry `fontVariant`
 * because RN types it as a mutable array and TYPE is `as const`.
 */
import React from 'react';
import {StyleSheet, Text, TextProps} from 'react-native';

import {TYPE} from '../theme/tokens';
import {useV2Theme} from '../theme/useV2Theme';

export type TxtVariant = keyof typeof TYPE;

/** the token keys a piece of text is allowed to take its colour from */
export type TxtTone =
  | 'ink'
  | 'ink2'
  | 'muted'
  | 'faint'
  | 'onPri'
  | 'onClock'
  | 'onAlt'
  | 'onEv'
  | 'danger'
  | 'onDanger'
  | 'pos'
  | 'ev'
  | 'clock'
  /** a form validation error (the classic lobby's FEN field) */
  | 'error'
  /** a textual link / toggle (the classic lobby's settings + controls rows) */
  | 'link';

export type TxtProps = TextProps & {
  variant: TxtVariant;
  tone?: TxtTone;
  /** a colour the model computed (the rating delta's sign, the low-time clock) */
  color?: string;
  /** tabular figures: clocks, ratings, notation, countdowns */
  numeric?: boolean;
};

function Txt({
  variant,
  tone = 'ink',
  color,
  numeric,
  style,
  ...rest
}: TxtProps) {
  const tokens = useV2Theme(s => s.tokens);
  return (
    <Text
      {...rest}
      style={[
        TYPE[variant],
        {color: color ?? tokens[tone]},
        numeric ? styles.numeric : null,
        style,
      ]}
    />
  );
}

const styles = StyleSheet.create({
  numeric: {
    fontVariant: ['tabular-nums'],
  },
});

export default React.memo(Txt);
